import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

// ── Context ────────────────────────────────────────────────────────────────────

export const LIST_COLORS = ['#B85450', '#E0A526', '#14504A', '#6B6760', '#14504A', '#EC4899', '#10B981', '#FBBF24'];

const ListsContext = createContext(null);

export function ListsProvider({ children }) {
  const [lists, setLists] = useState([]);

  const reload = useCallback(() => {
    fetch('/api/lists')
      .then(r => r.json())
      .then(d => setLists(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const memberSets = useMemo(() => {
    const m = {};
    for (const l of lists) m[l.id] = new Set(l.members || []);
    return m;
  }, [lists]);

  const value = useMemo(() => ({
    lists,
    reload,
    memberSet: (id) => memberSets[id] || new Set(),
    listsForItem: (type, id) => lists.filter(l => (memberSets[l.id] || new Set()).has(`${type}:${id}`)),
    async createList(name, color) {
      const r = await fetch('/api/lists', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, color }) });
      const data = await r.json();
      reload();
      return data;
    },
    async addItems(listId, items) {
      await fetch(`/api/lists/${listId}/items`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
      reload();
    },
    async removeItem(listId, item_type, item_id) {
      await fetch(`/api/lists/${listId}/items`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ item_type, item_id }) });
      reload();
    },
    async renameList(id, patch) {
      await fetch(`/api/lists/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
      reload();
    },
    async deleteList(id) {
      await fetch(`/api/lists/${id}`, { method: 'DELETE' });
      reload();
    },
  }), [lists, memberSets, reload]);

  return <ListsContext.Provider value={value}>{children}</ListsContext.Provider>;
}

export function useLists() {
  const ctx = useContext(ListsContext);
  if (!ctx) throw new Error('useLists must be used within ListsProvider');
  return ctx;
}

// ── Row checkbox ────────────────────────────────────────────────────────────────

export function RowCheckbox({ checked, onChange, title }) {
  return (
    <span
      role="checkbox"
      aria-checked={checked}
      title={title || 'Select'}
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 18, height: 18, borderRadius: 5, cursor: 'pointer', flexShrink: 0,
        border: `1.5px solid ${checked ? 'var(--teal)' : 'var(--navy-border)'}`,
        background: checked ? 'var(--teal)' : 'transparent', transition: 'all 0.12s',
      }}
    >
      {checked && (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#FAF7F2" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
      )}
    </span>
  );
}

// ── List membership dots (shows which lists an item belongs to) ─────────────────

export function ListDots({ type, id }) {
  const { listsForItem } = useLists();
  const member = listsForItem(type, id);
  if (member.length === 0) return null;
  return (
    <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }} title={member.map(l => l.name).join(', ')}>
      {member.slice(0, 4).map(l => (
        <span key={l.id} style={{ width: 7, height: 7, borderRadius: '50%', background: l.color || 'var(--teal)' }} />
      ))}
    </span>
  );
}

// ── Add-to-list dropdown ─────────────────────────────────────────────────────────

export function AddToListMenu({ items, label = 'Add to list', onDone }) {
  const { lists, addItems, removeItem, createList, memberSet } = useLists();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  if (!items || items.length === 0) return null;

  const allIn = (listId) => {
    const s = memberSet(listId);
    return items.every(it => s.has(`${it.item_type}:${it.item_id}`));
  };
  const someIn = (listId) => {
    const s = memberSet(listId);
    return items.some(it => s.has(`${it.item_type}:${it.item_id}`));
  };

  async function toggle(list) {
    setBusy(true);
    if (allIn(list.id)) {
      for (const it of items) await removeItem(list.id, it.item_type, it.item_id);
    } else {
      await addItems(list.id, items);
    }
    setBusy(false);
    onDone && onDone();
  }

  async function doCreate() {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    const list = await createList(name, LIST_COLORS[lists.length % LIST_COLORS.length]);
    if (list?.id) await addItems(list.id, items);
    setBusy(false);
    setNewName('');
    setCreating(false);
    onDone && onDone();
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8,
          background: 'var(--teal)', border: 'none', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
        {label}
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
      </button>

      {open && (
        <>
          <div onClick={() => { setOpen(false); setCreating(false); }} style={{ position: 'fixed', inset: 0, zIndex: 8000 }} />
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 8001,
            width: 240, background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
            borderRadius: 10, padding: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '4px 8px 8px' }}>
              Add {items.length} item{items.length === 1 ? '' : 's'} to…
            </div>
            <div style={{ maxHeight: 220, overflowY: 'auto' }}>
              {lists.length === 0 && <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: '6px 8px' }}>No lists yet — create one below.</div>}
              {lists.map(l => {
                const checked = allIn(l.id);
                const partial = !checked && someIn(l.id);
                return (
                  <div key={l.id} onClick={() => !busy && toggle(l)} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 6,
                    cursor: busy ? 'wait' : 'pointer', fontSize: 13, color: 'var(--gray-300)',
                  }}
                    onMouseOver={e => e.currentTarget.style.background = 'var(--navy)'}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{
                      width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                      border: `1.5px solid ${checked || partial ? l.color : 'var(--navy-border)'}`,
                      background: checked ? l.color : partial ? `${l.color}55` : 'transparent',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {checked && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#FAF7F2" strokeWidth="3.5"><polyline points="20 6 9 17 4 12" /></svg>}
                    </span>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: l.color, flexShrink: 0 }} />
                    <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.name}</span>
                    <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{l.count}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ borderTop: '1px solid var(--navy-border)', marginTop: 6, paddingTop: 6 }}>
              {creating ? (
                <div style={{ display: 'flex', gap: 6, padding: '2px 4px' }}>
                  <input autoFocus value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && doCreate()}
                    placeholder="List name" style={{ flex: 1, fontSize: 12, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', outline: 'none' }} />
                  <button onClick={doCreate} style={{ fontSize: 12, padding: '6px 10px', borderRadius: 6, background: 'var(--teal)', border: 'none', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Add</button>
                </div>
              ) : (
                <div onClick={() => setCreating(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: 'var(--teal)', fontWeight: 600 }}
                  onMouseOver={e => e.currentTarget.style.background = 'var(--navy)'}
                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                  New list
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── List filter dropdown (show only members of a list) ───────────────────────────

export function ListFilter({ value, onChange }) {
  const { lists } = useLists();
  return (
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
      title="Filter by custom list"
      style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${value ? 'var(--teal)' : 'var(--navy-border)'}`, background: value ? 'var(--teal-dim)' : 'var(--navy-card)', color: value ? 'var(--teal)' : 'var(--gray-300)', fontSize: 12, cursor: 'pointer', outline: 'none', fontWeight: value ? 700 : 400 }}
    >
      <option value="">All lists</option>
      {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.count})</option>)}
    </select>
  );
}

// ── Selection bar (appears when rows are checked) ────────────────────────────────

export function SelectionBar({ count, items, onClear, extra }) {
  if (count === 0) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', marginBottom: 12,
      background: 'var(--teal-dim)', border: '1px solid var(--teal)', borderRadius: 10,
    }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--teal)' }}>{count} selected</span>
      <div style={{ flex: 1 }} />
      {extra}
      <AddToListMenu items={items} onDone={onClear} />
      <button onClick={onClear} style={{ padding: '7px 12px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
        Clear
      </button>
    </div>
  );
}
